
import { User as CoreUser } from 'src/app/models';
import { UserApplication, Abbreviation, CompositeId } from './user-application.model';
import { UserRoleConstants } from '../utilities/constants.util';

function refineApplications(applications: UserApplication[]) {
  applications.forEach((app) => refineApplication.call(this, this.$applications, this.$userApplications, app));
}

function refineApplication(abbreviations: Map<Abbreviation, UserApplication>, map: Map<CompositeId, UserApplication>, app: UserApplication) {
  var abbr = app.abbreviation
    , existing = abbreviations.get(abbr)  // find duplicate, if already exists in distinct set.
    , multiclient = !!existing
    , duplicate = multiclient && map.get(existing.id)  // find duplicate in redundant set, if already exists in distinct set.
    , application = new UserApplication({ ...app, multiclient })  // prepare object to store
    , id = application.id
    ;

  if (multiclient) new UserApplication({ ...duplicate, multiclient });  // update existing object in Heap (both sets)
  map.set(id, application);  // add current item
  abbreviations.set(abbr, application);  // overwrite latest item (multiples of the same abbreviation may exist in map)
}

class UserProfile extends CoreUser {
  public $roles: Map<string, string> = new Map();
  public $applications: Map<Abbreviation, UserApplication> = new Map();  // stores generically (non-duplicate)
  public $userApplications: Map<CompositeId, UserApplication> = new Map();  // stores specifically to include duplicates
  public userId: number = -1;
  public email: string = '';
  public firstName: string = '';
  public lastName: string = '';
  public brandingImage: string = '';
  public brandingName: string = '';
  public corporationId: number = -1;
  public sessionTimeoutMinutes: number = -1;
  public isRootAdmin: boolean = false;
  public isNonSSOUser: boolean = true;
  public isTwoFactorEnabled: boolean = true;

  constructor(options: any = {}) {
    super(options || {});
    var options = options || {};  // can be null; use empty object ({}) to enforce optionless instance.

    this.userId = +options.userId;
    this.email = options.email || this.email;
    this.firstName = options.firstName || this.firstName;
    this.lastName = options.lastName || this.lastName;
    this.brandingImage = options.brandingImage || this.brandingImage;
    this.brandingName = options.brandingName || this.brandingName;
    this.roles = options.roles || this.roles;
    this.userApplications = options.userApplications || this.userApplications;
    this.corporationId = +options.corporationId || this.corporationId;
    this.sessionTimeoutMinutes = +options.sessionTimeoutMinutes || this.sessionTimeoutMinutes;
    this.isRootAdmin = options.isRootAdmin;
    this.isNonSSOUser = options.isNonSSOUser;
    this.isTwoFactorEnabled = options.isTwoFactorEnabled;

    return this;
  }

  get roles(): string[] { return Array.from(this.$roles.values()); }
  set roles(value: string[]) { this.$roles.clear(); value.forEach((role) => this.$roles.set(role, role)); }
  get userApplications(): UserApplication[] { return Array.from(this.$userApplications.values()); }
  set userApplications(value: UserApplication[]) { this.$userApplications.clear(); refineApplications.call(this, value); }
  get $$$isHuronAdmin(): boolean { return this.$roles.has('HuronAdministrator'); }
  get $$$isCorpAdmin(): boolean { return this.$roles.has('CorporationAdministrator'); }
  get $$$isHuronSupport(): boolean { return this.$roles.has('HuronSupport'); }
  get $$$isCoachImpersonate(): boolean { return this.$roles.has('CoachImpersonate'); }
  get $$$isUserAdministrator(): boolean { return this.$roles.has('UserAdministrator'); }
  get $$$isClientAdministrator(): boolean { return this.$roles.has('ClientAdministrator'); }
  get applications(): UserApplication[] { return Array.from(this.$applications.values()); }  // gets set in refineApplication
  get id() { return this.userId; }
  get $$$isRootAdmin(): boolean { return this.isRootAdmin; }
  get $$$isNonSSOUser(): boolean { return this.isNonSSOUser; }

  public isHuronSupport(): boolean {
    return this.$$$isHuronSupport;
  }
  public isHuronAdmin(): boolean {
    return this.$$$isHuronAdmin;
  }
  public isCorpAdmin(): boolean {
    return this.$$$isCorpAdmin;
  }
  public isCoachImpersonate(): boolean {
    return this.$$$isCoachImpersonate;
  }
  public isUserAdministrator(): boolean {
    return this.$$$isUserAdministrator;
  }
  public isClientAdministrator(): boolean {
    return this.$$$isClientAdministrator;
  }
  public hasApplication(applicationAbbreviation: string): boolean {
    return this.$applications.has(applicationAbbreviation);
  }  
  public getHapUserRole(): string {
    if(this.isRootAdmin) {
      return UserRoleConstants.RootAdmin;
    }
    if(this.$roles.has(UserRoleConstants.HuronAdministrator)) {
      return UserRoleConstants.HuronAdministrator;
    }
    if(this.$roles.has(UserRoleConstants.HuronSupport)) {
      return UserRoleConstants.HuronSupport;
    }
    if(this.$roles.has(UserRoleConstants.CoachImpersonate)) {
      return UserRoleConstants.CoachImpersonate;
    }
    if(this.$roles.has(UserRoleConstants.CorporationAdministrator)) {
      return UserRoleConstants.CorporationAdministrator;
    }
    if(this.$roles.has(UserRoleConstants.ClientAdministrator)) {
      return UserRoleConstants.ClientAdministrator;
    }
    if(this.$roles.has(UserRoleConstants.UserAdministrator)) {
      return UserRoleConstants.UserAdministrator;
    }

    return UserRoleConstants.User;
  }

  toJSON() {  // necessary for getters
    var o: any = {};
    ['userId', 'email', 'firstName', 'lastName', 'brandingImage', 'brandingName', 'roles', 'userApplications', 'sessionTimeoutMinutes', 'isRootAdmin', 'isNonSSOUser', 'isTwoFactorEnabled'].forEach((key) => o[key] = this[key]);
    return o;
  }
  valueOf() {
    return this.id;
  }

}

export { UserProfile };
